import type { ThemeId } from '../../theme/ThemeContext'
import { AsyncPullLoadingIndicator } from '../async/AsyncPullLoadingIndicator'
import { PageAsyncEmptyState } from '../async/PageAsyncEmptyState'
import { PageAsyncErrorState } from '../async/PageAsyncErrorState'

export type LibraryStateKind = 'loading' | 'empty' | 'error'

export type ThemedLibraryStateProps = {
  theme: ThemeId
  kind: LibraryStateKind
  /** kind=error 时展示 */
  detail?: string
  onRetry?: () => void
}

/** 列表加载失败：与空状态同谱的灰线稿，不额外引入警示色块 */
function ErrorCloudListIllustration({ angular }: { angular: boolean }) {
  const rx = angular ? 0 : 8
  const sw = angular ? 1.9 : 1.7
  return (
    <svg
      className="mx-auto h-[4.75rem] w-[4.75rem] shrink-0 text-ink-soft/50"
      viewBox="0 0 72 72"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <rect
        x="14"
        y="18"
        width="44"
        height="36"
        rx={rx}
        className="stroke-current"
        strokeWidth={sw}
        strokeOpacity={0.85}
      />
      <path
        d="M26 30h20M26 38h14M26 46h18"
        className="stroke-current"
        strokeWidth={sw * 0.75}
        strokeLinecap="round"
        strokeOpacity={0.45}
      />
      <circle
        cx="48"
        cy="26"
        r="9"
        className="stroke-current"
        strokeWidth={sw * 0.9}
        fill="var(--panel)"
        strokeOpacity={0.95}
      />
      <path
        d="M48 21.5v6.2"
        className="stroke-current"
        strokeWidth={sw * 0.95}
        strokeLinecap="round"
        strokeOpacity={0.92}
      />
      <circle cx="48" cy="31.8" r="1.15" className="fill-current" fillOpacity={0.85} />
    </svg>
  )
}

/** 云端参考图空状态：灰调线稿，表达「无内容」而非品牌强调色 */
function EmptyCloudReferenceIllustration({ angular }: { angular: boolean }) {
  const rx = angular ? 0 : 8
  const sw = angular ? 1.9 : 1.7
  return (
    <svg
      className="mx-auto h-[4.75rem] w-[4.75rem] shrink-0 text-ink-soft/45"
      viewBox="0 0 72 72"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <rect
        x="14"
        y="18"
        width="44"
        height="36"
        rx={rx}
        className="stroke-current"
        strokeWidth={sw}
        strokeOpacity={0.9}
      />
      <circle
        cx="44.5"
        cy="29"
        r="5"
        className="stroke-current"
        strokeWidth={sw * 0.88}
        fill="none"
        strokeOpacity={0.55}
      />
      <path
        d="M18.5 46.5c7-6 15.5-9 22-6.5 4.5 1.6 8.5 4.5 12.5 6.5"
        className="stroke-current"
        strokeWidth={sw}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeOpacity={0.6}
      />
    </svg>
  )
}

/**
 * 图库页云端列表：加载 / 空 / 错误的主题化占位（与像素、玻璃等工作台风格对齐）
 */
export function ThemedLibraryState({ theme, kind, detail, onRetry }: ThemedLibraryStateProps) {
  const angular = theme === 'pixel' || theme === 'geek'
  const terminalish = theme === 'geek'

  if (kind === 'loading') {
    return (
      <AsyncPullLoadingIndicator
        angular={angular}
        label={terminalish ? 'fetching_assets…' : '正在同步云端列表…'}
        className="pb-4"
      />
    )
  }

  if (kind === 'error') {
    return (
      <PageAsyncErrorState
        theme={theme}
        title={terminalish ? 'cloud_fetch_failed' : '云端列表加载失败'}
        detail={detail}
        onRetry={onRetry}
        illustration={<ErrorCloudListIllustration angular={angular} />}
      />
    )
  }

  return (
    <PageAsyncEmptyState
      illustration={<EmptyCloudReferenceIllustration angular={angular} />}
      title="暂无数据"
      titleTone="muted"
      aria-label="暂无保存的生成图"
      className="sm:py-24"
    />
  )
}
